/* eslint-disable no-console */
import { HubConnectionBuilder, LogLevel, HubConnectionState } from '@microsoft/signalr';
import logger from '@widgets/logger';

let Hub = null;
const ActiveEvents = new Set();
const MAX_RETRY_TIME = 5 * 60 * 1000; // 5 minutes

const getOrCreateHub = () => {
    if (!Hub) {
        const user = JSON.parse(localStorage.getItem('user'));
        const withCredentials = !window.location.origin.includes('localhost');
        Hub = new HubConnectionBuilder()
            .withUrl(`${process.env.REACT_APP_API_URL}websockets`, {
                logger: LogLevel.Information,
                accessTokenFactory: () => user.token,
                headers: { Authorization: 'Bearer ' + user.token },
                withCredentials,
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    if (retryContext.elapsedMilliseconds < MAX_RETRY_TIME) {
                        return 5000;
                    } else {
                        return null;
                    }
                },
            })
            .build();

        logger.info('Ws Hub created');

        Hub.onclose(() => {});
        Hub.onreconnecting(() => {});

        return Hub;
    } else {
        return Hub;
    }
};

const startHub = async () => {
    try {
        if (Hub && Hub.state === HubConnectionState.Disconnected) {
            await Hub.start();
            logger.info('Ws Hub: Started connection');
        }
    } catch (err) {
        logger.error('Ws Hub: Failed to start connection - ', err);
    }
};

const stopHub = async () => {
    try {
        if (Hub && Hub.state === HubConnectionState.Connected) {
            await Hub.stop();
            if (ActiveEvents.size > 0) {
                ActiveEvents.clear();
            }
            logger.info('Ws Hub: Stopped connection');
        }
    } catch (err) {
        logger.error('Ws Hub: Failed to stop connection - ', err);
    }
};

// INFO: https://learn.microsoft.com/en-us/aspnet/core/signalr/javascript-client?view=aspnetcore-9.0&tabs=visual-studio#call-hub-methods-from-the-client
const subscribeToEvents = (events, onReconnected) => {
    try {
        const hub = getOrCreateHub();

        for (const [event, handler] of Object.entries(events)) {
            /* Workaround to prevent multiple subscriptions to the same message
                INFO: https://stackoverflow.com/questions/73929390/remove-subscribed-handler-from-a-method-when-using-signalr-in-react
            */
            hub.off(event);
            hub.on(event, handler);
            ActiveEvents.add(event);
            logger.info('Ws Hub: subscribed to event - ', event);
        }
        if (onReconnected) {
            hub.onreconnected(() => {
                onReconnected();
            });
        }
        startHub();
    } catch (error) {
        logger.error('Ws Hub: subscribe to events - ', error);
    }
};

const unsubscribeFromEvents = events => {
    try {
        const hub = getOrCreateHub();

        events.forEach(event => {
            logger.info('Ws Hub: unsubscribing from event - ', event);
            hub.off(event);
            ActiveEvents.delete(event);
        });
    } catch (error) {
        logger.error('Ws Hub: unsubscribe - ', error);
    }
};

const isConnected = () => {
    return Hub && Hub.state === HubConnectionState.Connected;
};

const waitForConnection = (timeout = 30000) => {
    return new Promise((resolve, reject) => {
        if (isConnected()) {
            resolve();
            return;
        }

        const timeoutId = setTimeout(() => {
            reject(new Error(`Connection timeout after ${timeout}ms`));
        }, timeout);

        const checkConnection = () => {
            if (isConnected()) {
                clearTimeout(timeoutId);
                resolve();
            } else {
                setTimeout(checkConnection, 100);
            }
        };

        checkConnection();
    });
};

const sendEventNoResponse = async (event, ...args) => {
    if (!event || typeof event !== 'string') {
        throw new Error('Event name must be a non-empty string');
    }
    try {
        const hub = getOrCreateHub();
        if (!isConnected()) {
            await startHub();
            await waitForConnection();
        }

        logger.info(`Ws Hub: Sending event: '${event}'`, args);
        await hub.send(event, ...args);

        logger.info(`Ws Hub: Successfully sent event: '${event}'`);
    } catch (error) {
        logger.error(`Ws Hub: Failed to send event: '${event}'`, error);
        throw new Error(`Failed to send event '${event}': ${error.message}`);
    }
};

export const hubService = {
    subscribeToEvents,
    unsubscribeFromEvents,
    stopHub,
    sendEventNoResponse,
};
